import type { RefObject } from 'react';
import React from 'react';

import { Field, useLabelClasses } from '@flywire/headlessui-tailwindcss';

import { VisuallyHidden } from 'components/ui/VisuallyHidden/VisuallyHidden';
import type { InputProps } from 'hooks/useInput/useInput';
import { useInput } from 'hooks/useInput/useInput';
import { useInputAriaProps } from 'hooks/useInputAriaProps/useInputAriaProps';
import { useTranslations } from 'utils/translations/useTranslations';

import '../Input.scss';

const TextareaInput = ({
  'aria-describedby': ariaDescribedBy,
  className,
  disabled = false,
  error,
  floatingLabel = true,
  forwardRef,
  hint = '',
  label,
  name,
  id = name,
  onBlur,
  onChange,
  onFocus,
  readOnly = false,
  required,
  value = '',
  ...props
}: InputProps) => {
  const { fieldValue, handleBlur, handleChange, handleFocus } = useInput({
    disabled,
    label,
    name,
    onBlur,
    onChange,
    onFocus,
    readOnly,
    value,
  });
  const labelClasses = useLabelClasses({
    floatingLabel,
    className:
      'peer-placeholder-shown:top-4 peer-placeholder-shown:translate-y-0',
  });
  const { inputAriaProps } = useInputAriaProps({
    'aria-describedby': ariaDescribedBy,
    disabled,
    error,
    hint,
    label,
    name,
    readOnly,
    required,
  });
  const i18n = useTranslations();

  return (
    <Field id={id} floatingLabel={floatingLabel} className={className}>
      <div className="relative">
        <Field.Textarea
          {...props}
          disabled={disabled}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={label}
          readOnly={readOnly}
          ref={forwardRef as RefObject<HTMLTextAreaElement>}
          required={required}
          value={fieldValue}
          {...inputAriaProps}
        />
        {label && (
          <Field.Label id={`${id}-label`} className={labelClasses}>
            {label}
          </Field.Label>
        )}
      </div>
      {hint && (
        <Field.Hint className="mb-0" data-testid={`${name}-hint`}>
          <span data-testid={`${name}-hint-msg`} id={`${name}-hint-msg`}>
            {hint}
          </span>
        </Field.Hint>
      )}
      {error && typeof error === 'string' && (
        <Field.Error className="mb-0" data-testid={`${name}-error`}>
          <VisuallyHidden as="span">{label}</VisuallyHidden>
          <span data-testid={`${name}-error-msg`} id={`${name}-error-msg`}>
            {i18n.t(error)}
          </span>
        </Field.Error>
      )}
    </Field>
  );
};

export { TextareaInput };
